import React from 'react';
import { Container, Typography, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { motion } from 'framer-motion';
import PolicyIcon from '@mui/icons-material/Policy';
import SecurityIcon from '@mui/icons-material/Security';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import CampaignIcon from '@mui/icons-material/Campaign';
import UpdateIcon from '@mui/icons-material/Update';
import { GlassPaper, fadeInUp, scaleIn } from '../../styles/styledComponents';
import { styled } from '@mui/material/styles';

const MotionBox = motion(Box);

const PageContainer = styled(Container)(({ theme }) => ({
  fontFamily: '"Orbitron", "Noto Sans JP", sans-serif',
  paddingTop: theme.spacing(10),
  paddingBottom: theme.spacing(8),
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  
  '&::before': {
    content: '""',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      radial-gradient(circle at 20% 50%, rgba(0, 255, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(255, 0, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 40% 20%, rgba(0, 255, 0, 0.1) 0%, transparent 50%)
    `,
    pointerEvents: 'none',
    zIndex: -1,
  },
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 900,
  background: 'linear-gradient(45deg, #00ffff 0%, #ff00ff 50%, #00ff00 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  marginBottom: theme.spacing(2),
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  textAlign: 'center',
  filter: 'drop-shadow(0 0 30px rgba(0, 255, 255, 0.5))',
}));

const SectionBox = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(5),
  position: 'relative',
  paddingLeft: theme.spacing(2),
  
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: '3px',
    background: `linear-gradient(180deg, ${theme.palette.neon.cyan}, transparent)`,
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  color: theme.palette.neon.cyan,
  marginBottom: theme.spacing(2),
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  
  '& .MuiSvgIcon-root': {
    filter: `drop-shadow(0 0 10px ${theme.palette.neon.cyan})`,
  },
}));

const PolicyText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  lineHeight: 1.8,
  fontFamily: '"Rajdhani", "Noto Sans JP", sans-serif',
  fontSize: '0.95rem',
}));

const PolicyList = styled(Box)(({ theme }) => ({
  listStyle: 'none',
  padding: 0,
  margin: theme.spacing(2, 0),
  
  '& li': {
    position: 'relative',
    paddingLeft: theme.spacing(3),
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontSize: '0.95rem',
    lineHeight: 1.6,
    fontFamily: '"Rajdhani", "Noto Sans JP", sans-serif',
    
    '&::before': {
      content: '"▸"',
      position: 'absolute',
      left: 0,
      color: theme.palette.neon.magenta,
      fontWeight: 700,
      fontSize: '1.2rem',
      filter: `drop-shadow(0 0 5px ${theme.palette.neon.magenta})`,
    },
  },
}));

const BackButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(45deg, #00ffff 0%, #00aaff 100%)',
  color: '#000',
  padding: theme.spacing(1.5, 4),
  borderRadius: 0,
  border: '2px solid #00ffff',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  boxShadow: '0 0 20px rgba(0, 255, 255, 0.5)',
  transition: 'all 0.3s ease',
  
  '&:hover': {
    background: 'linear-gradient(45deg, #00ffff 0%, #00aaff 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 0 30px rgba(0, 255, 255, 0.8)',
    borderColor: '#66ffff',
  },
}));

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <PageContainer maxWidth="md">
      <MotionBox
        initial="initial"
        animate="animate"
        exit="exit"
        variants={fadeInUp}
      >
        <Box sx={{ mb: 6 }}>
          <PageTitle variant="h3" component="h1">
            Privacy Policy
          </PageTitle>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              color: 'text.secondary',
              maxWidth: '600px',
              margin: '0 auto',
              textAlign: 'center',
              fontWeight: 500,
              letterSpacing: '0.05em',
            }}
          >
            ClashSyncにおけるユーザー情報の取り扱いについて
          </Typography>
        </Box>

        <GlassPaper
          component={motion.div}
          variants={scaleIn}
          initial="initial"
          animate="animate"
          sx={{ p: 5, mb: 4 }}
        >
          <SectionBox>
            <SectionTitle variant="h5">
              <PolicyIcon />
              第1条（個人情報）
            </SectionTitle>
            <PolicyText>
              「個人情報」とは，個人情報保護法にいう「個人情報」を指すものとし，生存する個人に関する情報であって，当該情報に含まれる氏名，生年月日，住所，電話番号，連絡先その他の記述等により特定の個人を識別できる情報及び容貌，指紋，声紋にかかるデータ，及び健康保険証の保険者番号などの当該情報単体から特定の個人を識別できる情報（個人識別情報）を指します。
            </PolicyText>
          </SectionBox>

          <SectionBox>
            <SectionTitle variant="h5">
              <SecurityIcon />
              第2条（個人情報の収集方法）
            </SectionTitle>
            <PolicyText>
              ClashSyncは，ユーザーが利用登録をする際にメールアドレスなどの個人情報をお尋ねすることがあります。
            </PolicyText>
          </SectionBox>

          <SectionBox>
            <SectionTitle variant="h5">
              <SecurityIcon />
              第3条（個人情報を収集・利用する目的）
            </SectionTitle>
            <PolicyText>
              ClashSyncが個人情報を収集・利用する目的は，以下のとおりです：
            </PolicyText>
            <PolicyList component="ul">
              <li>ClashSyncサービスの提供・運営のため</li>
              <li>ユーザーからのお問い合わせに回答するため（本人確認を行うことを含む）</li>
              <li>メンテナンス，重要なお知らせなど必要に応じたご連絡のため</li>
              <li>利用規約に違反したユーザーや，不正・不当な目的でサービスを利用しようとするユーザーの特定をし，ご利用をお断りするため</li>
              <li>上記の利用目的に付随する目的</li>
            </PolicyList>
          </SectionBox>

          <SectionBox>
            <SectionTitle variant="h5">
              <CampaignIcon />
              第4条（広告の配信について）
            </SectionTitle>
            <PolicyText sx={{ mb: 2 }}>
              当サイトでは、第三者配信の広告サービスを利用しています：
            </PolicyText>
            <PolicyList component="ul">
              <li>ユーザーの興味に応じた広告を表示するため、Cookieを使用する場合があります</li>
              <li>ユーザーは、ブラウザの設定でCookieを無効にすることで、パーソナライズ広告を無効にすることができます</li>
            </PolicyList>
          </SectionBox>

          <SectionBox>
            <SectionTitle variant="h5">
              <AnalyticsIcon />
              第5条（アクセス解析ツールについて）
            </SectionTitle>
            <PolicyText sx={{ mb: 2 }}>
              当サイトでは、サービスの品質向上のため、アクセス解析ツールを使用しています：
            </PolicyText>
            <PolicyList component="ul">
              <li>ページの閲覧状況</li>
              <li>サイトの訪問経路</li>
              <li>滞在時間等の情報</li>
            </PolicyList>
            <PolicyText sx={{ mt: 2 }}>
              これらの情報は匿名で収集され、個人を特定する目的では使用されません。
            </PolicyText>
          </SectionBox>

          <SectionBox>
            <SectionTitle variant="h5">
              <UpdateIcon />
              第6条（プライバシーポリシーの変更）
            </SectionTitle>
            <PolicyText sx={{ mb: 2 }}>
              本ポリシーの内容は，法令その他本ポリシーに別段の定めのある事項を除いて，ユーザーに通知することなく，変更することができるものとします。
            </PolicyText>
            <PolicyText>
              ClashSyncが別途定める場合を除いて，変更後のプライバシーポリシーは，本ウェブサイトに掲載したときから効力を生じるものとします。
            </PolicyText>
          </SectionBox>
        </GlassPaper>

        <Box display="flex" justifyContent="center">
          <BackButton
            component={motion.button}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
          >
            Back to Home
          </BackButton>
        </Box>
      </MotionBox>
    </PageContainer>
  );
};

export default PrivacyPolicy;
